import React, { useCallback, useRef } from "react";
import { useRawJwt } from "~/Auth/AuthenticationManager";
import { apiFetch, HttpError } from "~/api-fetch/apiFetch";
import { reportError } from "~/error-handler/reportError";
import ImageLoader from "~/visuals/organisms/Expenses/ExpenseForm/ImageLoader";
import FileUploadFormField from "~/visuals/organisms/FileUploadFormField";

type ImageUploadWrapperProps = {
  imageUrl: string | null;
  setImageUrl: React.Dispatch<string | null>;
  setImageId: React.Dispatch<string | null>;
  label: string;
  formField: string;
  required?: boolean;
};
export const ImageUploadWrapper: React.FC<ImageUploadWrapperProps> = ({
  setImageId,
  label,
  formField,
  imageUrl,
  setImageUrl,
  required,
}) => {
  const token = useRawJwt();
  const reportErrorRef = useRef<((message: string) => void) | null>(null);

  const uploadImage = async (value: File) => {
    const formData = new FormData();
    formData.append("files", value);
    try {
      const res = await apiFetch(
        "/api/upload-image",
        {
          method: "post",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: formData,
        },
        { operation: "uploadImage" }
      );

      if (!res.ok) {
        reportError(HttpError.fromResponse(res));
        reportErrorRef.current?.("Upload failed. Please try again.");
        return;
      }

      const result = await res.json();
      if (result.url && result.imageId) {
        setImageUrl(result.url);
        setImageId(result.imageId);
      } else {
        reportError(new Error("Upload response missing url or imageId"));
        reportErrorRef.current?.("Upload failed. Please try again.");
      }
    } catch (err) {
      reportError(err);
      reportErrorRef.current?.("Upload failed. Please try again.");
    }
  };

  const handleImageChange = (file: File) => {
    void uploadImage(file);
  };

  const handleImageRemove = () => {
    setImageUrl(null);
    setImageId(null);
  };

  const handleError = useCallback((reportError: (message: string) => void) => {
    reportErrorRef.current = reportError;
  }, []);

  return (
    <>
      <ImageLoader className={`${label}-image`} imageUrl={imageUrl} />
      <FileUploadFormField
        label={label}
        helperText={`Upload image of ${label}.`}
        formField={formField}
        validMessage="Please provide a valid file"
        handleChange={handleImageChange}
        handleRemove={handleImageRemove}
        required={required ?? false}
        onError={handleError}
      />
    </>
  );
};
